/**
 * Typed wrappers around /api/projects. apiFetch attaches the bearer +
 * handles single-flight refresh — these helpers should never be used
 * with raw fetch.
 *
 * The PATCH wrapper accepts any subset of `ProjectInput` (mirroring the
 * server's zod `.partial()`). DELETE returns void — the server sends
 * 204 on success.
 */
import type { Project, ProjectInput } from "@shared/types";
import { apiFetch } from "./api";

export type ProjectPatchInput = Partial<ProjectInput>;

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // ignore — body might not be JSON
    }
    const err = new Error(`api ${res.status}`) as Error & { status: number; body: unknown };
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return (await res.json()) as T;
}

export async function listProjects(): Promise<Project[]> {
  const res = await apiFetch("/api/projects");
  return readJson<Project[]>(res);
}

export async function getProject(id: number): Promise<Project> {
  const res = await apiFetch(`/api/projects/${id}`);
  return readJson<Project>(res);
}

export async function createProject(input: ProjectInput): Promise<Project> {
  const res = await apiFetch("/api/projects", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  return readJson<Project>(res);
}

export async function updateProject(id: number, patch: ProjectPatchInput): Promise<Project> {
  const res = await apiFetch(`/api/projects/${id}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  return readJson<Project>(res);
}

export async function deleteProject(id: number): Promise<void> {
  const res = await apiFetch(`/api/projects/${id}`, { method: "DELETE" });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // ignore
    }
    const err = new Error(`api ${res.status}`) as Error & { status: number; body: unknown };
    err.status = res.status;
    err.body = body;
    throw err;
  }
}
